import type { FeePolicy } from "@/lib/pay/fees";
import { generateRandomKey } from "@/lib/random-string";

export const PAY_ID_LENGTH = 13;
export const PAY_SET_ID_LENGTH = 13;

export const BUILTIN_PAY_CODES = ["wechatpay", "alipay", "balancepay"] as const;

export type BuiltinPayCode = (typeof BUILTIN_PAY_CODES)[number];

export const PAY_CODE_PATTERN = /^[a-z][a-z0-9]{0,31}$/;

export const PAY_CODE_INPUT_MAX_LENGTH = 32;

export function sanitizePayCodeInput(value: string) {
  let cleaned = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, PAY_CODE_INPUT_MAX_LENGTH);
  cleaned = cleaned.replace(/^[^a-zA-Z]+/, "");
  return cleaned;
}

export function sanitizePayConfigKeyInput(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, "");
}

export function isValidPayCode(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return false;
  }

  return PAY_CODE_PATTERN.test(trimmed.toLowerCase());
}

export type PayConfigEntry = {
  label: string;
  key: string;
  value: string;
};

export const DEFAULT_PAY_SORT_ORDER = 100;

export function normalizePaySortOrder(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value) && value !== 0) {
    return Math.floor(value);
  }

  return DEFAULT_PAY_SORT_ORDER;
}

export type PayMethod = {
  payId: string;
  code: string;
  name: string;
  enabled: boolean;
  terminals: string[];
  config: PayConfigEntry[];
  feePolicy?: FeePolicy;
  sortOrder: number;
  createdAt: string;
};

export type PaySetItem = {
  setId: string;
  name: string;
  enabled: boolean;
  sortOrder: number;
  createdAt: string;
};

function comparePayCreatedAt(left: string, right: string) {
  const leftMs = Date.parse(left);
  const rightMs = Date.parse(right);
  const leftValue = Number.isFinite(leftMs) ? leftMs : 0;
  const rightValue = Number.isFinite(rightMs) ? rightMs : 0;

  return leftValue - rightValue;
}

export type PayPageData = {
  methods: PayMethod[];
  sets: PaySetItem[];
};

export const DEFAULT_PAY_SET_ITEMS: PaySetItem[] = [
  { setId: "webPage000001", name: "电脑网页", enabled: true, sortOrder: 10, createdAt: "" },
  { setId: "mobilePage001", name: "手机网页", enabled: true, sortOrder: 11, createdAt: "" },
  { setId: "miniProgram01", name: "小程序端", enabled: true, sortOrder: 12, createdAt: "" },
  { setId: "harmonyOS0001", name: "鸿蒙OS", enabled: true, sortOrder: 13, createdAt: "" },
  { setId: "android000001", name: "安卓APP", enabled: true, sortOrder: 14, createdAt: "" },
  { setId: "apple00000001", name: "苹果iOS", enabled: true, sortOrder: 15, createdAt: "" },
];

export const DEFAULT_PAY_SET_IDS = [
  DEFAULT_PAY_SET_ITEMS[0].setId,
  DEFAULT_PAY_SET_ITEMS[1].setId,
];

export const DEFAULT_BUILTIN_PAY_METHODS: PayMethod[] = [
  {
    payId: "payWechat0001",
    code: "wechatpay",
    name: "微信支付",
    enabled: false,
    terminals: DEFAULT_PAY_SET_IDS,
    config: [],
    sortOrder: 10,
    createdAt: "",
  },
  {
    payId: "payAlipay0001",
    code: "alipay",
    name: "支付宝支付",
    enabled: false,
    terminals: DEFAULT_PAY_SET_IDS,
    config: [],
    sortOrder: 11,
    createdAt: "",
  },
  {
    payId: "payBalance001",
    code: "balancepay",
    name: "余额支付",
    enabled: true,
    terminals: DEFAULT_PAY_SET_IDS,
    config: [],
    sortOrder: 200,
    createdAt: "",
  },
];

export const DEFAULT_PAY_PAGE_DATA: PayPageData = {
  methods: DEFAULT_BUILTIN_PAY_METHODS.map((item) => ({
    ...item,
    terminals: [...item.terminals],
    config: item.config.map((entry) => ({ ...entry })),
  })),
  sets: DEFAULT_PAY_SET_ITEMS.map((item) => ({ ...item })),
};

export function migrateLegacyPayCode(code: string): string {
  if (code === "wechat") {
    return "wechatpay";
  }

  if (code === "balance") {
    return "balancepay";
  }

  return code;
}

export function isBuiltinPayCode(code: string): code is BuiltinPayCode {
  return (BUILTIN_PAY_CODES as readonly string[]).includes(
    migrateLegacyPayCode(code),
  );
}

export const INTEGRATED_PAY_CODES: readonly BuiltinPayCode[] = ["balancepay", "wechatpay", "alipay"];

export function isPayCodeIntegrated(code: string): boolean {
  return (INTEGRATED_PAY_CODES as readonly string[]).includes(
    migrateLegacyPayCode(code.trim().toLowerCase()),
  );
}

export const BUILTIN_PAY_SET_IDS = DEFAULT_PAY_SET_ITEMS.map((item) => item.setId);

export function isBuiltinPaySetId(setId: string): boolean {
  return BUILTIN_PAY_SET_IDS.includes(setId);
}

export function createPayMethod(
  existingPayIds: string[],
  _existingCodes: string[],
): PayMethod {
  let payId = generateRandomKey(PAY_ID_LENGTH);

  while (existingPayIds.includes(payId)) {
    payId = generateRandomKey(PAY_ID_LENGTH);
  }

  return {
    payId,
    code: "",
    name: "",
    enabled: true,
    terminals: [],
    config: [],
    sortOrder: DEFAULT_PAY_SORT_ORDER,
    createdAt: new Date().toISOString(),
  };
}

export function createPaySet(existingSetIds: string[]): PaySetItem {
  let setId = generateRandomKey(PAY_SET_ID_LENGTH);

  while (existingSetIds.includes(setId)) {
    setId = generateRandomKey(PAY_SET_ID_LENGTH);
  }

  return {
    setId,
    name: "",
    enabled: true,
    sortOrder: DEFAULT_PAY_SORT_ORDER,
    createdAt: new Date().toISOString(),
  };
}

export function sortPayMethods(items: PayMethod[], savedItems: PayMethod[]) {
  const savedIndex = new Map(
    savedItems.map((item, index) => [item.payId, index]),
  );

  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const sortDiff = left.item.sortOrder - right.item.sortOrder;

      if (sortDiff !== 0) {
        return sortDiff;
      }

      const createdAtDiff = comparePayCreatedAt(
        left.item.createdAt,
        right.item.createdAt,
      );

      if (createdAtDiff !== 0) {
        return createdAtDiff;
      }

      const leftSaved = savedIndex.get(left.item.payId);
      const rightSaved = savedIndex.get(right.item.payId);

      if (leftSaved != null && rightSaved != null) {
        return leftSaved - rightSaved;
      }

      return left.index - right.index;
    })
    .map(({ item }) => item);
}

export function sortPaySets(items: PaySetItem[], savedItems: PaySetItem[]) {
  const savedIndex = new Map(
    savedItems.map((item, index) => [item.setId, index]),
  );

  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const sortDiff = left.item.sortOrder - right.item.sortOrder;

      if (sortDiff !== 0) {
        return sortDiff;
      }

      const createdAtDiff = comparePayCreatedAt(
        left.item.createdAt,
        right.item.createdAt,
      );

      if (createdAtDiff !== 0) {
        return createdAtDiff;
      }

      const leftSaved = savedIndex.get(left.item.setId);
      const rightSaved = savedIndex.get(right.item.setId);

      if (leftSaved != null && rightSaved != null) {
        return leftSaved - rightSaved;
      }

      return left.index - right.index;
    })
    .map(({ item }) => item);
}

export function getPaySetOptions(sets: PaySetItem[] | undefined | null) {
  return (sets ?? [])
    .filter((item) => item.enabled)
    .map((item) => ({
      value: item.setId,
      label: item.name || item.setId,
    }));
}

export function createUniquePayId(existingIds: string[]) {
  let payId = generateRandomKey(PAY_ID_LENGTH);

  while (existingIds.includes(payId)) {
    payId = generateRandomKey(PAY_ID_LENGTH);
  }

  return payId;
}

export function createUniquePaySetId(existingIds: string[]) {
  let setId = generateRandomKey(PAY_SET_ID_LENGTH);

  while (existingIds.includes(setId)) {
    setId = generateRandomKey(PAY_SET_ID_LENGTH);
  }

  return setId;
}

import { isSensitiveConfigKey } from "@/lib/sensitive-config-key";

export function isSensitivePayConfigKey(key: string) {
  return isSensitiveConfigKey(key);
}
